import React, { useEffect, useState } from 'react'
import { Check, Download, Info } from 'lucide-react'
import { FaApple, FaChrome, FaWindows } from 'react-icons/fa6'
import { cn } from '@/lib/utils'
import { SettingsCard } from './SettingsCard'

type Platform = 'mac' | 'windows' | 'chromeos'
const LOGO = 'https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png'

const STEPS: Record<Platform, string[]> = {
  mac: [
    'Click Install Athios above. In Safari, choose File › Add to Dock.',
    'Confirm in the prompt that appears.',
    'Athios opens in its own window and appears in your Dock and Launchpad.',
  ],
  windows: [
    'Click Install Athios above (Chrome or Edge).',
    'Follow your browser’s install prompt.',
    'Athios opens in its own window and appears in your Start menu.',
  ],
  chromeos: [
    'Click Install Athios above.',
    'Follow the install prompt.',
    'Athios opens in its own window and appears in your launcher.',
  ],
}

const detect = (): Platform => {
  const ua = navigator.userAgent
  if (/CrOS/.test(ua)) return 'chromeos'
  if (/Windows/.test(ua)) return 'windows'
  return 'mac'
}

/** Settings > App: install Athios as an app on this computer */
export const AppTab: React.FC = () => {
  const [prompt, setPrompt] = useState<any>(() => (window as any).__athiosInstallPrompt ?? null)
  const [installed, setInstalled] = useState(
    () => window.matchMedia?.('(display-mode: standalone)').matches ?? false,
  )
  const [platform, setPlatform] = useState<Platform>(detect)
  const [note, setNote] = useState<string | null>(null)

  useEffect(() => {
    const ready = () => setPrompt((window as any).__athiosInstallPrompt ?? null)
    const done = () => setInstalled(true)
    window.addEventListener('athios:installable', ready)
    window.addEventListener('appinstalled', done)
    return () => {
      window.removeEventListener('athios:installable', ready)
      window.removeEventListener('appinstalled', done)
    }
  }, [])

  const install = async () => {
    if (!prompt) {
      setNote(
        /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)
          ? 'In Safari, choose File › Add to Dock.'
          : 'Use the install icon in your browser’s address bar, or open the browser menu and choose Install Athios.',
      )
      return
    }
    prompt.prompt()
    const choice = await prompt.userChoice.catch(() => null)
    if (choice?.outcome === 'accepted') setInstalled(true)
    ;(window as any).__athiosInstallPrompt = null
    setPrompt(null)
  }

  return (
    <div className="max-w-3xl">
      <SettingsCard
        title="Install Athios"
        subtitle="Get quick access to Athios from your desktop or home screen."
      >
        <div className="flex flex-col items-center gap-6 rounded-xl border border-border p-6 sm:flex-row">
          <span className="flex h-28 w-28 shrink-0 items-center justify-center rounded-2xl bg-[#F4FBEA]">
            <img src={LOGO} alt="" className="h-16 w-16" />
          </span>
          <div className="w-full space-y-3">
            <ul className="space-y-1.5 text-sm text-foreground">
              {[
                'Open Athios in its own app window',
                'Get to your campaigns directly',
                'Receive browser notifications',
                'Works on Mac, Windows and Chromebook',
              ].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-[#16A34A]" strokeWidth={3} /> {t}
                </li>
              ))}
            </ul>
            {installed ? (
              <p className="inline-flex items-center gap-2 rounded-xl bg-[#E8F9D2] px-4 py-2.5 font-semibold text-[#2F5E06]">
                <Check className="h-4 w-4" /> Athios is installed
              </p>
            ) : (
              <button
                type="button"
                onClick={install}
                className="inline-flex h-11 items-center gap-2 rounded-xl bg-[#C0FD72] px-6 font-semibold text-[#0F172A] hover:bg-[#B2F25E]"
              >
                <Download className="h-4 w-4" /> Install Athios
              </button>
            )}
            {note ? <p className="text-sm text-muted-foreground">{note}</p> : null}
            <p className="text-xs text-muted-foreground">
              Works in Chrome, Edge, Safari and other modern browsers.
            </p>
          </div>
        </div>

        <h3 className="mt-6 font-semibold text-foreground">How to install</h3>
        <div className="mt-2 grid grid-cols-3 gap-1 rounded-xl bg-muted p-1">
          {(
            [
              ['mac', 'Mac', FaApple],
              ['windows', 'Windows', FaWindows],
              ['chromeos', 'ChromeOS', FaChrome],
            ] as const
          ).map(([id, label, Icon]) => (
            <button
              key={id}
              type="button"
              onClick={() => setPlatform(id)}
              aria-pressed={platform === id}
              className={cn(
                'inline-flex h-10 items-center justify-center gap-2 rounded-lg text-sm font-medium',
                platform === id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
              )}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
        <ol className="mt-4 space-y-3">
          {STEPS[platform].map((s, i) => (
            <li key={s} className="flex gap-3 text-sm text-foreground">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#C0FD72] text-xs font-bold text-[#0F172A]">
                {i + 1}
              </span>
              {s}
            </li>
          ))}
        </ol>
        <p className="mt-5 flex gap-2 rounded-xl border border-[#BFD7FB] bg-[#EFF5FF] px-4 py-3 text-sm text-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" />
          Don't see the install option? Use an up-to-date Chrome, Edge or Safari. Some browsers,
          like Firefox, don't install web apps.
        </p>
      </SettingsCard>
    </div>
  )
}

export default AppTab
